import { Injectable, inject } from '@angular/core';
import {Observable, Subject} from "rxjs";
import {OngoingRequestTrackerService, SessionService} from "@core";
import {HttpClient} from "@angular/common/http";
import {ShiftSplit} from "../../models/shift-split.model";
import {ShiftSplitResult} from "../../models/shift-split-result.model";
import {EmployeeTask, EmployeeView, EmployeeWorkShift, ProblemInstance} from "../../models";
import {AbstractControl, FormGroup} from "@angular/forms";
import {TimeService} from "@shared";
import {ProblemInstanceService} from "../problem-instance/problem-instance.service";

@Injectable({
    providedIn: 'root'
})
export class ShiftSplitService {
    private readonly sessionService = inject(SessionService);
    private readonly httpClient = inject(HttpClient);
    private readonly ongoingRequestTracker = inject(OngoingRequestTrackerService);
    private readonly timeService = inject(TimeService);
    private readonly problemInstanceService = inject(ProblemInstanceService);


    public updateEmployeeTasks$: Subject<void>;

    public static DEFAULT_SPLIT_TASK_NAME = "Opptatt";

    constructor() {
        this.updateEmployeeTasks$ = new Subject();
    }

    public splitShift(shiftSplit: ShiftSplit): Observable<ShiftSplitResult> {
        const url = `${this.sessionService.getApiUrl()}/split_shift/${this.sessionService.requestId}`;
        const body = {
            workShift: shiftSplit,
        };
        const requestOptions = this.ongoingRequestTracker.createTrackingEnabledRequestOptions();

        return this.httpClient.post<ShiftSplitResult>(url, body, requestOptions);
    }

    public createShiftSplitTask(employee: EmployeeView, splitForm: FormGroup, problemInstance: ProblemInstance): EmployeeTask {
        const employeeTask = {
            taskId: this.generateSplitTaskId(employee),
            originalTaskId: this.generateSplitTaskId(employee),
            fromTime: splitForm.controls['fromTime'].value.replace(':', ""),
            toTime: splitForm.controls['toTime'].value.replace(':', ""),
            fromDate: employee.fromDate,
            toDate: employee.toDate,
            duration: this.timeService.timeDifference(splitForm.controls['fromTime'].value, splitForm.controls['toTime'].value),
            employeeId: employee.employeeId,
            employeeTaskType: 'SPECIFIED_GE_TASK',
            fixedToEmployee: true,
            isShiftSplitTask: true,
            isHeavy: false,
            serviceName: this.generateSplitTaskName(splitForm.controls['taskName']),
            isActive: true,
            isSyncedInitially: false,
            workHourId: employee.workHourId,
            skillRequirement: employee.skillLevelId,
            numberOfHigherSkillLevelsAccepted: 1000,
            doesRequirePhysicalAppearance: true,
            rosterDepartmentId: employee.rosterDepartmentId,
            streetAddressNameNumberAndLetter: problemInstance.officeInfo.streetAddressNameNumberAndLetter,
            zipCode: problemInstance.officeInfo.zipCode,
            shiftType: employee.shiftType,
            addressIsValid: true,
            allowedShiftCodes: [],
            prohibitedShiftCodes: []
        } as EmployeeTask;
        return employeeTask;
    }

    public updateShiftSplitTask(employee: EmployeeView, splitForm: FormGroup, problemInstance: ProblemInstance) {
        const splitTask =
            problemInstance.emTasksNotPatients
                .find(task => task.isShiftSplitTask && task.employeeId === employee.employeeId);
        splitTask.fromTime = splitForm.controls['fromTime'].value.replace(':', "");
        splitTask.toTime = splitForm.controls['toTime'].value.replace(':', "");
        splitTask.duration = this.timeService.timeDifference(splitForm.controls['fromTime'].value, splitForm.controls['toTime'].value);
        splitTask.serviceName = this.generateSplitTaskName(splitForm.controls['taskName']);
    }

    public checkSplitWindowUpdate(employeeWorkShift: EmployeeWorkShift) {
        this.problemInstanceService.getProblemInstance().subscribe(tasks =>
        {
            const splitTask = tasks.problemInstance.emTasksNotPatients.find(task => task.isShiftSplitTask && task.employeeId === employeeWorkShift.employeeId);
            if (splitTask) {
                if (this.timeService.convertTime(splitTask.fromTime, true) < this.timeService.convertTime(employeeWorkShift.fromTime, true)) {
                    splitTask.fromTime = employeeWorkShift.fromTime;
                }
                if (this.timeService.convertTime(splitTask.toTime, true) > this.timeService.convertTime(employeeWorkShift.toTime, true)) {
                    splitTask.toTime = employeeWorkShift.toTime;
                }
                this.updateEmployeeTasks$.next();
            }
        });
    }

    private generateSplitTaskId(employee: EmployeeView) {
        return employee.workHourId + "-" + employee.employeeId + "-split";
    }

    private generateSplitTaskName(nameControl: AbstractControl): string {
        if (!nameControl.value && nameControl.value.length < 1) {
            return ShiftSplitService.DEFAULT_SPLIT_TASK_NAME;
        } else {
            return nameControl.value;
        }
    }
}
